// src/layouts/LoggedInLayout/LoggedInLayout.tsx
import React, { useState, Suspense, useCallback, useEffect, useRef } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import Header from "@/PageLayout/PrivateLayout/Header/Header";
import Sidebar from "@/PageLayout/PrivateLayout/Sidebar/Siderbar";
import { LoadingSpinner, AIChatWidget } from "@/Components/common";
import { resolveBreadcrumbTarget, isSidebarSectionLabel } from "./breadcrumbNavigator";
import "./LoggedInLayout.css";

const LoggedInLayout: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const contentRef = useRef<HTMLDivElement>(null);

  // Tag breadcrumb crumbs that map to sidebar section labels (Tools,
  // User Management, Content Management, etc.) so CSS can render them
  // as plain non-actionable hierarchy markers — no pointer cursor and
  // no hover affordance. The click delegate already returns null for
  // these via resolveBreadcrumbTarget, this just makes them *look* the
  // way they behave.
  useEffect(() => {
    const root = contentRef.current;
    if (!root) return;

    let scheduled = false;
    // Run via a microtask (not requestAnimationFrame): microtasks drain before
    // the browser paints, so a top-level "Section › Page" breadcrumb is hidden
    // before it's ever shown — no one-frame flash. The `scheduled` flag still
    // coalesces bursts of mutations into a single pass.
    const schedule =
      typeof queueMicrotask === 'function'
        ? queueMicrotask
        : (cb: () => void) => Promise.resolve().then(cb);
    const tag = () => {
      if (scheduled) return;
      scheduled = true;
      schedule(() => {
        scheduled = false;
        const containers = root.querySelectorAll('[class*="breadcrumb" i]');
        containers.forEach((container) => {
          // The non-separator crumb labels in this container, in order.
          const crumbs = Array.from(container.children).filter((child) => {
            const cls = (child.getAttribute('class') || '').toLowerCase();
            if (/separator/.test(cls)) return false;
            const t = (child.textContent || '').trim();
            return !!t && !/^[›>»\/|→]+$/.test(t);
          });

          // Hide-until-nested: a trail that is only "Section › Page" (<= 2 crumbs
          // rooted at a non-navigable sidebar section) adds nothing, so hide the
          // whole breadcrumb. Show it only when there's a real navigable parent
          // (genuine nesting, e.g. "Marketing › Campaign"). Items/separators
          // (which also match the selector) have no crumb children, so they
          // never trigger this.
          if (crumbs.length >= 1) {
            const firstText = (crumbs[0].textContent || '').trim();
            const topLevelSectionTrail =
              crumbs.length <= 2 && isSidebarSectionLabel(firstText);
            (container as HTMLElement).style.display = topLevelSectionTrail ? 'none' : '';
          }

          Array.from(container.children).forEach((child) => {
            const cls = (child.getAttribute('class') || '').toLowerCase();
            if (/separator/.test(cls)) return;
            const text = (child.textContent || '').trim();
            if (!text) return;
            if (isSidebarSectionLabel(text)) {
              child.setAttribute('data-breadcrumb-disabled', 'true');
            } else if (child.getAttribute('data-breadcrumb-disabled') === 'true') {
              child.removeAttribute('data-breadcrumb-disabled');
            }
          });
        });
      });
    };

    tag();
    const observer = new MutationObserver(tag);
    observer.observe(root, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  // Click delegation: existing pages render breadcrumbs as plain <span>s
  // grouped inside a container whose className contains "breadcrumb". We
  // intercept clicks here so every non-current crumb becomes navigable
  // without touching any per-page JSX, logic, or styling.
  const handleContentClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;

      // Closest crumb element (span/button/a) inside a breadcrumb container.
      const crumb = target.closest('span, button, a') as HTMLElement | null;
      if (!crumb) return;
      const container = crumb.closest('[class*="breadcrumb" i]') as HTMLElement | null;
      if (!container) return;

      // Make sure crumb is a direct or near-direct child of the container,
      // not a separate breadcrumb-shaped element nested elsewhere.
      if (!container.contains(crumb)) return;

      const crumbCls = (crumb.getAttribute('class') || '').toLowerCase();
      // Skip the container itself if it bubbled
      if (crumb === container) return;
      // Skip separators, current/active markers, and any pre-wired clickable
      // crumbs (those already have their own onClick — don't double-handle).
      if (/separator|current|active|clickable/.test(crumbCls)) return;
      // Skip pure separator text (›, >, &gt;)
      const text = (crumb.textContent || '').trim();
      if (!text || /^[›>»\/|→]+$/.test(text)) return;

      // Identify the current-page crumb: typically the last non-separator
      // child. If the crumb we clicked is the last navigable child, treat it
      // as the current page and do nothing.
      const navigableChildren = Array.from(container.children).filter((el) => {
        const c = (el.getAttribute('class') || '').toLowerCase();
        if (/separator/.test(c)) return false;
        const t = (el.textContent || '').trim();
        if (!t || /^[›>»\/|→]+$/.test(t)) return false;
        return true;
      });
      if (navigableChildren.length > 0 && navigableChildren[navigableChildren.length - 1] === crumb) {
        return;
      }

      const targetPath = resolveBreadcrumbTarget(text, location.pathname);
      if (!targetPath || targetPath === location.pathname) return;

      e.preventDefault();
      e.stopPropagation();
      navigate(targetPath);
    },
    [navigate, location.pathname]
  );

  return (
    <div className="logged-in-container">
      {/* Header */}
      <div className="logged-in-header-wrapper">
        <Header
          isMobileMenuOpen={isMobileMenuOpen}
          setIsMobileMenuOpen={setIsMobileMenuOpen}
        />
      </div>

      {/* Mobile Drawer – ALWAYS EXPANDED */}
      <div
        className={`mobile-sidebar-overlay ${isMobileMenuOpen ? 'open' : ''}`}
        onClick={() => setIsMobileMenuOpen(false)}
      />
      <div className={`mobile-sidebar-drawer ${isMobileMenuOpen ? 'open' : ''}`}>
        <button
          className="mobile-sidebar-close"
          onClick={() => setIsMobileMenuOpen(false)}
          aria-label="Close menu"
        >
          ×
        </button>
        <Sidebar forceExpanded={true} />   {/* THIS LINE CHANGED */}
      </div>

      {/* Desktop Layout */}
      <div className="logged-in-main">
        {/* Desktop Sidebar – normal hover behavior */}
        <div className="logged-in-sidebar-wrapper">
          <Sidebar />   {/* No prop = normal hover expand */}
        </div>

        <div className="logged-in-content" onClick={handleContentClick} ref={contentRef}>
          <Suspense fallback={<LoadingSpinner fullScreen size="lg" />}>
            <Outlet />
          </Suspense>
        </div>
      </div>
      <AIChatWidget />
    </div>
  );
};

export default LoggedInLayout;